// Nav level meter — Photon's AudioLevelMeter (Figma node 5953:2423, designer
// 08-28/08-31): an 8px bar made of two 3px pill tracks (L over R, 2px gap)
// flush with the island's content, lit fill in --72, 2px peak line in --96,
// Gray 40 at 56% as the track; seven 8px monospace labels (−60 … 0) 4px below
// the bar, evenly distributed so "-60" is flush left and "0" flush right.
// Bypass: fill and peak switch to opaque Gray 40.
//
// Ballistics live in state/meter.ts; this class only draws. It is driven once
// per animation frame while audio plays (and for a short tail afterwards).

import { FONT_MONO } from '@/styles/fonts';
import {
  type ChannelMeter, dbToFrac, isSettled, silentChannel, stepChannel,
} from '@/state/meter';

const BAR_TOP = 6;        // (28 − 8) / 2 − 4: bar top at the design's y in the 28px child
const BAR_H = 3;
const BAR_GAP = 2;
// Labels: the app draws them centred in an 8px box 4px below the bar with a
// JUCE font of HEIGHT 8 (ascent + descent), which is an em size of ~6.9px for
// a monospace face — not a CSS 8px em.
const LABEL_BOX_TOP = BAR_TOP + 2 * BAR_H + BAR_GAP + 4;
const LABEL_BOX_H = 8;
const LABEL_PX = 6.9;
const LABELS = ['-60', '-50', '-40', '-30', '-20', '-10', '0'];

const TRACK = 'rgba(86, 95, 102, 0.56)';   // gray40 @ 56%
const FILL = '#a9b1b8';                     // --72
const PEAK = '#f5f5f5';                     // --96
const BYPASS = '#565f66';                   // gray40, opaque
const LABEL = '#a9b1b8';

export class LevelMeterCanvas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private widthCss = 0;
  private heightCss = 0;
  private dpr = 1;
  private resizeObserver: ResizeObserver;
  private channels: [ChannelMeter, ChannelMeter] = [silentChannel(), silentChannel()];
  private lastMs = 0;
  private bypass = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to acquire 2D canvas context');
    this.ctx = ctx;
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(canvas);
    this.handleResize();
  }

  setBypass(bypass: boolean): void {
    if (bypass === this.bypass) return;
    this.bypass = bypass;
    this.draw();
  }

  setSilence(): void {
    this.channels = [silentChannel(), silentChannel()];
    this.draw();
  }

  // Feed the current output peaks (dBFS). Returns true while something is
  // still moving, so the caller knows whether to schedule another frame.
  update(leftDb: number, rightDb: number, nowMs: number): boolean {
    const dt = this.lastMs ? nowMs - this.lastMs : 16;
    this.lastMs = nowMs;
    this.channels = [
      stepChannel(this.channels[0], leftDb, nowMs, dt),
      stepChannel(this.channels[1], rightDb, nowMs, dt),
    ];
    this.draw();
    return !(isSettled(this.channels[0]) && isSettled(this.channels[1]));
  }

  dispose(): void {
    this.resizeObserver.disconnect();
  }

  private handleResize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width));
    const h = Math.max(1, Math.round(rect.height));
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (w === this.widthCss && h === this.heightCss && dpr === this.dpr) return;
    this.widthCss = w;
    this.heightCss = h;
    this.dpr = dpr;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.draw();
  }

  private pill(x: number, y: number, w: number, h: number): void {
    const r = h / 2;
    const { ctx } = this;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arc(x + w - r, y + r, r, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(x + r, y + h);
    ctx.arc(x + r, y + r, r, Math.PI / 2, (3 * Math.PI) / 2);
    ctx.closePath();
  }

  private drawBar(y: number, ch: ChannelMeter): void {
    const { ctx, widthCss: w } = this;
    this.pill(0, y, w, BAR_H);
    ctx.fillStyle = TRACK;
    ctx.fill();

    const levelX = dbToFrac(ch.level) * w;
    if (levelX > 0) {
      // Rounded fill clipped to the pill so the right edge stays square
      // while it grows, and both ends stay round at the extremes.
      ctx.save();
      this.pill(0, y, w, BAR_H);
      ctx.clip();
      ctx.fillStyle = this.bypass ? BYPASS : FILL;
      ctx.fillRect(0, y, levelX, BAR_H);
      ctx.restore();
    }

    if (ch.peak > -60) {
      const peakX = dbToFrac(ch.peak) * w;
      ctx.fillStyle = this.bypass ? BYPASS : PEAK;
      ctx.fillRect(Math.min(w - 2, Math.max(0, peakX - 1)), y, 2, BAR_H);
    }
  }

  private draw(): void {
    const { ctx, widthCss: w, heightCss: h } = this;
    ctx.clearRect(0, 0, w, h);
    if (w < 40) return;

    this.drawBar(BAR_TOP, this.channels[0]);
    this.drawBar(BAR_TOP + BAR_H + BAR_GAP, this.channels[1]);

    ctx.fillStyle = LABEL;
    ctx.font = `${LABEL_PX}px ${FONT_MONO}`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    const lastW = ctx.measureText(LABELS[LABELS.length - 1]).width;
    const step = (w - lastW) / (LABELS.length - 1);
    const labelY = LABEL_BOX_TOP + LABEL_BOX_H / 2;
    for (let i = 0; i < LABELS.length; i++) {
      ctx.fillText(LABELS[i], Math.round(step * i), labelY);
    }
  }
}
